import { useState, useEffect, useRef, useCallback } from 'react';

// `performance.memory` (heap size info) and `PerformanceResourceTiming`'s
// `transferSize` are real fields Chrome exposes, but the first is a
// nonstandard Chromium-only extension TypeScript's DOM lib does not declare,
// and the second is only present on the resource-timing subtype that
// `getEntriesByType` doesn't narrow to. Both are typed locally rather than
// reached for with `as any`.
interface PerformanceMemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemoryInfo;
}

interface PerformanceThresholds {
  memory: number;
  renderTime: number;
  apiResponse: number;
  bundleSize: number;
}

interface PerformanceAlert {
  type: 'render' | 'memory' | 'fps' | 'network' | 'bundle';
  message: string;
  severity: 'warning' | 'error';
  timestamp: number;
}

interface NetworkRequestRecord {
  id: string;
  url: string;
  method: string;
  startTime: number;
  status: 'pending' | 'success' | 'error';
  endTime?: number;
  duration?: number;
  responseSize?: number | string;
  error?: string;
}

interface PerformanceMemoryMetric {
  used: number;
  total: number;
  percentage: number;
  limit?: number;
}

interface PerformanceMetrics {
  memory: PerformanceMemoryMetric;
  renderTime: number;
  fps: number;
  bundleSize: number;
  networkRequests: NetworkRequestRecord[];
  componentRenders: number;
  alerts: PerformanceAlert[];
}

interface WebVitals {
  LCP: number | null;
  FID: number | null;
  FCP: number | null;
  TTFB: number | null;
  CLS: number | null;
}

interface PerformanceReport {
  timestamp: number;
  metrics: PerformanceMetrics;
  webVitals: WebVitals;
  recommendations: string[];
}

interface UsePerformanceMonitorOptions {
  enableRealTimeMetrics?: boolean;
  enableMemoryMonitoring?: boolean;
  // Not `Partial<PerformanceThresholds>`: the destructuring default below
  // only ever applies as one complete object (it only triggers when the
  // whole `thresholds` key is omitted), so a caller-supplied value has
  // always had to carry all four fields -- no caller (App.tsx is the only
  // one, and it doesn't pass `thresholds` at all) has ever exercised a
  // partial override.
  thresholds?: PerformanceThresholds;
  onAlert?: ((alert: PerformanceAlert) => void) | null;
}

/**
 * Performance monitoring hook with real-time metrics and alerts
 */
export const usePerformanceMonitor = (options: UsePerformanceMonitorOptions = {}) => {
  const {
    enableRealTimeMetrics = true,
    enableMemoryMonitoring = true,
    thresholds = {
      memory: 100 * 1024 * 1024, // 100MB
      renderTime: 16, // 16ms (60fps)
      apiResponse: 3000, // 3 seconds
      bundleSize: 5 * 1024 * 1024 // 5MB
    },
    onAlert = null // Performance alert callback - used in addAlert
  } = options;

  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    memory: { used: 0, total: 0, percentage: 0 },
    renderTime: 0,
    fps: 60,
    bundleSize: 0,
    networkRequests: [],
    componentRenders: 0,
    alerts: []
  });

  const frameCountRef = useRef(0);
  const lastFrameTimeRef = useRef(performance.now());
  const renderStartTimeRef = useRef(0);
  const alertsRef = useRef<PerformanceAlert[]>([]);
  const networkRequestsRef = useRef<NetworkRequestRecord[]>([]);

  // Web Vitals metrics
  const [webVitals, setWebVitals] = useState<WebVitals>({
    LCP: null, // Largest Contentful Paint
    FID: null, // First Input Delay
    FCP: null, // First Contentful Paint
    TTFB: null, // Time to First Byte
    CLS: null // Cumulative Layout Shift
  });

  // Alert management - defined early to avoid use-before-define
  const addAlert = useCallback((alert: PerformanceAlert) => {
    alertsRef.current.push(alert);

    // Update alerts in metrics without causing re-renders
    setMetrics(prev => ({
      ...prev,
      alerts: [...alertsRef.current].slice(-20) // Keep last 20 alerts
    }));

    // Use ref to access current onAlert callback to avoid dependency
    if (options.onAlert) {
      options.onAlert(alert);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies to prevent infinite loops

  // Component render tracking
  const trackRender = useCallback((componentName: string) => {
    renderStartTimeRef.current = performance.now();

    return () => {
      const renderTime = performance.now() - renderStartTimeRef.current;

      setMetrics(prev => ({
        ...prev,
        renderTime,
        componentRenders: prev.componentRenders + 1
      }));

      // Alert if render time exceeds threshold
      if (renderTime > thresholds.renderTime) {
        addAlert({
          type: 'render',
          message: `Slow render detected in ${componentName}: ${renderTime.toFixed(2)}ms`,
          severity: 'warning',
          timestamp: Date.now()
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholds.renderTime]); // addAlert is now stable

  // Memory monitoring
  const monitorMemory = useCallback(() => {
    const perf = performance as PerformanceWithMemory;
    if (!enableMemoryMonitoring || !perf.memory) return;

    const memory: PerformanceMemoryMetric = {
      used: perf.memory.usedJSHeapSize,
      total: perf.memory.totalJSHeapSize,
      limit: perf.memory.jsHeapSizeLimit,
      percentage: (perf.memory.usedJSHeapSize / perf.memory.jsHeapSizeLimit) * 100
    };

    setMetrics(prev => ({ ...prev, memory }));

    // Memory threshold alert
    if (memory.used > thresholds.memory) {
      addAlert({
        type: 'memory',
        message: `High memory usage: ${(memory.used / 1024 / 1024).toFixed(2)}MB`,
        severity: 'error',
        timestamp: Date.now()
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMemoryMonitoring, thresholds.memory]); // addAlert is now stable

  // FPS monitoring
  const monitorFPS = useCallback(() => {
    const now = performance.now();
    const delta = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;
    frameCountRef.current++;

    // Calculate FPS every second
    if (frameCountRef.current % 60 === 0) {
      const fps = Math.round(1000 / delta);
      setMetrics(prev => ({ ...prev, fps }));

      // Low FPS alert
      if (fps < 30) {
        addAlert({
          type: 'fps',
          message: `Low FPS detected: ${fps}`,
          severity: 'warning',
          timestamp: Date.now()
        });
      }
    }

    if (enableRealTimeMetrics) {
      requestAnimationFrame(monitorFPS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableRealTimeMetrics]); // addAlert is now stable

  // Network monitoring
  const trackNetworkRequest = useCallback((url: string, method = 'GET') => {
    const startTime = performance.now();
    const requestId = `${method}-${url}-${startTime}`;

    const request: NetworkRequestRecord = {
      id: requestId,
      url,
      method,
      startTime,
      status: 'pending'
    };

    networkRequestsRef.current.push(request);

    return {
      success: (response?: Response) => {
        const endTime = performance.now();
        const duration = endTime - startTime;

        const updatedRequest: NetworkRequestRecord = {
          ...request,
          endTime,
          duration,
          status: 'success',
          responseSize: response?.headers?.get?.('content-length') || 0
        };

        updateNetworkRequest(requestId, updatedRequest);

        // Slow API response alert
        if (duration > thresholds.apiResponse) {
          addAlert({
            type: 'network',
            message: `Slow API response: ${url} took ${duration.toFixed(2)}ms`,
            severity: 'warning',
            timestamp: Date.now()
          });
        }
      },
      error: (error: unknown) => {
        const endTime = performance.now();
        const duration = endTime - startTime;

        const updatedRequest: NetworkRequestRecord = {
          ...request,
          endTime,
          duration,
          status: 'error',
          error: error instanceof Error ? error.message : String(error)
        };

        updateNetworkRequest(requestId, updatedRequest);

        addAlert({
          type: 'network',
          message: `Network request failed: ${url} - ${error instanceof Error ? error.message : String(error)}`,
          severity: 'error',
          timestamp: Date.now()
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholds.apiResponse]); // addAlert is now stable

  const updateNetworkRequest = (requestId: string, updatedRequest: NetworkRequestRecord) => {
    networkRequestsRef.current = networkRequestsRef.current.map(req =>
      req.id === requestId ? updatedRequest : req
    );

    setMetrics(prev => ({
      ...prev,
      networkRequests: [...networkRequestsRef.current].slice(-50) // Keep last 50 requests
    }));
  };

  const clearAlerts = useCallback(() => {
    alertsRef.current = [];
    setMetrics(prev => ({ ...prev, alerts: [] }));
  }, []);

  // Web Vitals monitoring
  const trackWebVitals = useCallback(() => {
    // LCP - Largest Contentful Paint
    if ('PerformanceObserver' in window) {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        setWebVitals(prev => ({ ...prev, LCP: lastEntry.startTime }));
      });

      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.warn('LCP observation not supported');
      }

      // FCP - First Contentful Paint
      const fcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach(entry => {
          if (entry.name === 'first-contentful-paint') {
            setWebVitals(prev => ({ ...prev, FCP: entry.startTime }));
          }
        });
      });

      try {
        fcpObserver.observe({ entryTypes: ['paint'] });
      } catch (e) {
        console.warn('FCP observation not supported');
      }
    }

    // TTFB - Time to First Byte
    if (performance.timing) {
      const ttfb = performance.timing.responseStart - performance.timing.requestStart;
      setWebVitals(prev => ({ ...prev, TTFB: ttfb }));
    }
  }, []);

  // Bundle size tracking
  const trackBundleSize = useCallback(() => {
    if (performance.getEntriesByType) {
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const jsResources = resources.filter(resource =>
        resource.name.includes('.js') && resource.name.includes('static')
      );

      const totalSize = jsResources.reduce((sum, resource) =>
        sum + (resource.transferSize || 0), 0
      );

      setMetrics(prev => ({ ...prev, bundleSize: totalSize }));

      if (totalSize > thresholds.bundleSize) {
        addAlert({
          type: 'bundle',
          message: `Large bundle size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`,
          severity: 'warning',
          timestamp: Date.now()
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thresholds.bundleSize]); // addAlert is now stable

  // Performance report generation
  const generateReport = useCallback((): PerformanceReport => {
    const report: PerformanceReport = {
      timestamp: Date.now(),
      metrics: { ...metrics },
      webVitals: { ...webVitals },
      recommendations: []
    };

    // Add recommendations based on metrics
    if (metrics.memory.percentage > 80) {
      report.recommendations.push('Consider optimizing memory usage - reduce object references and use lazy loading');
    }

    if (metrics.fps < 50) {
      report.recommendations.push('Frame rate is low - consider optimizing animations and reducing DOM manipulations');
    }

    if (metrics.bundleSize > thresholds.bundleSize) {
      report.recommendations.push('Bundle size is large - consider code splitting and lazy loading');
    }

    const slowRequests = metrics.networkRequests.filter(req => (req.duration || 0) > 2000);
    if (slowRequests.length > 0) {
      report.recommendations.push(`${slowRequests.length} slow network requests detected - consider caching or API optimization`);
    }

    return report;
  }, [metrics, webVitals, thresholds.bundleSize]);

  // Initialize monitoring
  useEffect(() => {
    if (enableRealTimeMetrics) {
      const memoryInterval = setInterval(monitorMemory, 5000); // Every 5 seconds
      monitorFPS(); // Start FPS monitoring
      trackWebVitals();
      trackBundleSize();

      return () => {
        clearInterval(memoryInterval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableRealTimeMetrics]); // Only depend on the boolean flag to prevent infinite loops

  return {
    metrics,
    webVitals,
    trackRender,
    trackNetworkRequest,
    addAlert,
    clearAlerts,
    generateReport
  };
};

export default usePerformanceMonitor;
